import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type GetMultipartPartResponse } from '@air/api/types';
import { callWithRetry, getExponentialBackoff } from '@air/call-with-retry';
import { NetworkStatusInfo } from '@air/classes-network-status-info';
import {
  setUploadFailedAction,
  UploadStatus,
  uploadByIdSelector,
  uploadLogger,
  useUploaderStore,
} from '@air/redux-uploader';

import { UPLOAD_NETWORK_STATUS_CHECK_TIMEOUT_MS } from '../constants';
import { getUploadUrlForRetry, getUploadUrlType } from '../getUploadUrlForRetry';
import { isRetryableUploadTransferError } from '../isRetryableUploadTransferError';
import { isAbortError } from '../uploadErrors';
import { abortUpload } from '../utilities';
import { type UploadFileChunkParams, useUploadLargeFileChunk } from './useUploadLargeFileChunk';

const MAX_RETRIES = 5;

export const useUploadLargeFileChunkWithRetries = () => {
  const store = useUploaderStore();
  const dispatch = useDispatch();
  const { uploadLargeFileChunk } = useUploadLargeFileChunk();

  const uploadLargeFileChunkWithRetries = useCallback(
    (params: UploadFileChunkParams) => {
      let retryCount = 0;
      let partUploadUrlInfo: GetMultipartPartResponse | undefined;
      let currentUploadUrl: string | undefined;

      const getCurrentUploadUrlType = () =>
        partUploadUrlInfo && currentUploadUrl
          ? getUploadUrlType({ currentUrl: currentUploadUrl, uploadUrlInfo: partUploadUrlInfo })
          : 'primary';

      const getPartUrl = async (partNumber: number): Promise<GetMultipartPartResponse> => {
        if (!partUploadUrlInfo) {
          partUploadUrlInfo = await params.getPartUrl(partNumber);
          currentUploadUrl = partUploadUrlInfo.url;
        }

        return {
          ...partUploadUrlInfo,
          url: currentUploadUrl ?? partUploadUrlInfo.url,
        };
      };

      return callWithRetry({
        action: () => uploadLargeFileChunk({ ...params, getPartUrl, maxRetryCount: MAX_RETRIES, retryCount }),
        getRetryDelay: (retries) =>
          getExponentialBackoff({
            attempt: retries,
            factor: 2,
            initialDelay: 200,
            maxDelay: 30_000,
          }),
        onError: (error) => {
          console.info('error caught during upload', error);

          const updatedUpload = uploadByIdSelector(store.getState(), params.upload.id);

          /** if they paused the upload, don't report error */
          if (updatedUpload?.status === UploadStatus.paused) {
            if (isAbortError(error)) {
              params.onPartError?.({
                abortReason: 'pause',
                error,
                isOnline: NetworkStatusInfo.isOnline,
                isRetryable: false,
                maxRetryCount: MAX_RETRIES,
                partNumber: params.partNumber,
                retryCount,
                upload: params.upload,
                uploadUrlType: getCurrentUploadUrlType(),
              });
            }

            return;
          }

          params.onPartError?.({
            abortReason: isAbortError(error) && !updatedUpload ? 'cancel' : 'transfer_aborted',
            error,
            isOnline: NetworkStatusInfo.isOnline,
            isRetryable: isRetryableUploadTransferError(error),
            maxRetryCount: MAX_RETRIES,
            partNumber: params.partNumber,
            retryCount,
            upload: params.upload,
            uploadUrlType: getCurrentUploadUrlType(),
          });
        },
        shouldRetry: async (retries, error) => {
          const updatedUpload = uploadByIdSelector(store.getState(), params.upload.id);

          console.group('shouldRetry');
          console.info('updatedUpload', updatedUpload);
          console.info('partNumber', params.partNumber);
          console.info('retries', retries);
          console.info('error', error);
          console.groupEnd();

          /**
           * If the upload doesn't exist (the user might've cancled it),
           * don't bother retrying
           */
          if (!updatedUpload) {
            return false;
          }

          /** if they paused the upload, don't retry*/
          if (updatedUpload.status === UploadStatus.paused) {
            return false;
          }

          if (!isRetryableUploadTransferError(error)) {
            return false;
          }

          /** if they've hit the max number of retries, prevent a retry */
          if (retries >= MAX_RETRIES) {
            return false;
          }

          const isOnline = await NetworkStatusInfo.checkIfOnline({ timeout: UPLOAD_NETWORK_STATUS_CHECK_TIMEOUT_MS });

          console.info('isOnline', isOnline);

          if (!isOnline) {
            return false;
          }

          retryCount = retries + 1;

          if (partUploadUrlInfo && currentUploadUrl) {
            const nextUploadUrl = getUploadUrlForRetry({
              currentUrl: currentUploadUrl,
              error,
              uploadUrlInfo: partUploadUrlInfo,
            });

            if (nextUploadUrl !== currentUploadUrl) {
              uploadLogger.fallbackUrlSelected({ partNumber: params.partNumber, uploadId: params.upload.id });
            }

            currentUploadUrl = nextUploadUrl;
          }

          return true;
        },
      }).catch((error) => {
        /**
         * A part failed terminally (callWithRetry gave up). Mark the whole upload as failed so a
         * full `upload_error` is reported — but skip aborts (pause/cancel), and only do it once
         * since sibling chunk tasks may fail at the same time.
         */
        if (!isAbortError(error)) {
          const updatedUpload = uploadByIdSelector(store.getState(), params.upload.id);

          if (updatedUpload && updatedUpload.status !== UploadStatus.failed) {
            abortUpload(updatedUpload);
            dispatch(setUploadFailedAction({ errorMessage: 'Failed', uploadId: params.upload.id }));
            params.onUploadFailed?.({
              error,
              upload: params.upload,
              uploadUrlType: updatedUpload.uploadUrlType ?? getCurrentUploadUrlType(),
            });
          }
        }

        throw error;
      });
    },
    [dispatch, store, uploadLargeFileChunk],
  );

  return {
    uploadLargeFileChunkWithRetries,
  };
};
